import type { QueryClient } from "@tanstack/react-query";
import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";

import { wasExpired } from "../features/auth/expiry";
import { AppShell } from "../features/home/AppShell";
import { recentProjects } from "../features/home/recent";

/** The signed-in app; anyone else goes to sign in, and back to the page they were on when their session expired. Until the practice's first cycle is open, the home is the set-up's
 * welcome; a page reached by its address opens, and a connection to Instagram comes back to the home's notice,
 * which the home then takes out of its address without leaving. */
export const Route = createFileRoute("/_app")({
  beforeLoad: async ({ context, location, cause }) => {
    if (context.session.status !== "signedIn") {
      throw redirect(
        wasExpired() ? { to: "/sign-in", search: { reason: "expired", redirect: location.href } } : { to: "/sign-in" },
      );
    }
    if (location.pathname !== "/" || cause === "stay" || "instagram" in location.search) {
      return;
    }
    if (!(await cycleOpened(context.queryClient))) {
      throw redirect({ to: "/onboarding" });
    }
  },
  component: function App() {
    return (
      <AppShell>
        <Outlet />
      </AppShell>
    );
  },
});

/** Whether a cycle of the practice is open; a home whose projects cannot be read opens as it is, and says so. */
async function cycleOpened(queryClient: QueryClient): Promise<boolean> {
  try {
    // Asked for here, apart from the first screen's code, which stays free of the generated client.
    const { getListProjectsQueryOptions } = await import("../api/gen/endpoints/projects/projects");
    const projects = await queryClient.ensureQueryData(getListProjectsQueryOptions(recentProjects));
    return projects.items.some((project) => project.cycle !== undefined);
  } catch {
    return true;
  }
}
